import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import {
  api,
  RequestError,
  type StagePositions,
  type VerificationDetail as Detail,
} from '../lib/api.ts'
import { PrescriptionViewer } from '../components/PrescriptionViewer.tsx'
import { Coachmark, coachmarkDismissed, dismissCoachmarkForever } from '../components/Coachmark.tsx'
import { AddToGoldenSet } from '../components/AddToGoldenSet.tsx'
import { ConferenceHistory } from '../components/ConferenceHistory.tsx'
import { ReaderObject } from '../components/ReaderObject.tsx'
import { StatusPopover } from '../components/StatusPopover.tsx'
import { Tooltip } from '../components/Tooltip.tsx'
import { NOT_A_DIVERGENCE } from '../components/DecisionActions.tsx'
import { useToast } from '../components/Toast.tsx'
import { useConfirm } from '../components/Confirm.tsx'
import { sessionEmail } from '../lib/session.ts'
import { STAGE_LABEL, isAnalysing, stageOf } from '../lib/vocabulary.ts'
import { useConferenceClaim } from '../lib/useConferenceClaim.ts'
import { useLateErp } from '../lib/useLateErp.ts'
import { usePrescriptionPages } from '../lib/usePrescriptionPages.ts'
import { prescriptionFileBase } from '../lib/prescriptionDocument.ts'
import { useManualAttachments, shownPages } from '../lib/useManualAttachments.ts'
import {
  AttachReceitaButton,
  ManualAttachmentError,
  ManualReceitaMark,
} from '../components/ManualAttachments.tsx'
import { useVerificationProgress, withProgress } from '../lib/useVerificationProgress.ts'
import { ComparisonCard } from '../components/ComparisonCard.tsx'
import { QuotationStep } from '../components/QuotationOnly.tsx'
import { ProductionSheet } from '../components/ProductionSheet.tsx'
import { RuleOutcomes } from '../components/RuleOutcomes.tsx'
import { LabelPanel } from '../components/LabelPrint.tsx'
import { parseProductionSheet } from '../lib/productionSheet.ts'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GoldenCaseScore } from '../components/GoldenCaseScore.tsx'
import { CopiedFromNotice } from '../components/CopiedFromNotice.tsx'
import { OutdatedEvaluationNotice } from '../components/OutdatedEvaluationNotice.tsx'
import { CopyToSimulationControl } from '../components/CopyToSimulationControl.tsx'
import { useCopyToSimulation } from '../lib/useCopyToSimulation.ts'
import { toPath } from '../lib/route.ts'
import {
  activesBoard,
  columnStatusFor,
  parseSide,
  worstStatus,
  type Side,
} from '../lib/verificationItems.ts'
import {
  buildGuidedSteps,
  namesFinding,
  openingForFinding,
  openingPins,
  railCounterText,
  railCounts,
  railTone,
  unresolvedFindingCount,
  unresolvedStepIndexes,
  type GuidedStep,
  type OpenRow,
} from '../lib/guidedSteps.ts'
import { titleCaseName } from '../lib/titleCase.ts'
import { FeedbackButton } from '../components/FeedbackButton.tsx'
import { GO_TO_REQUISITION_KEY, rowIntent, screenIntent } from '../lib/shortcuts.ts'
import { GoToRequisition } from '../components/GoToRequisition.tsx'
import { Progress } from '../components/Progress.tsx'
import { ProgressTrack } from '../components/ProgressTrack.tsx'
import { CardHeader, PANEL } from '../components/CardHeader.tsx'
import { FOCUS_RING } from '../components/focusRing.ts'
import { ReviewView, correctionSteps, leavesOnReverify } from '../lib/corrections.ts'
import { disagreementPins, firstStopOn, reviewTour } from '../lib/adversarialReview.ts'
import { useAdversarialReview } from '../lib/useAdversarialReview.ts'
import { useReviewWalk } from '../lib/useReviewWalk.ts'
import { AdversarialReviewControl } from '../components/AdversarialReviewControl.tsx'
import { AdversarialReviewModal } from '../components/AdversarialReviewModal.tsx'
import { AdversarialReviewTour } from '../components/AdversarialReviewTour.tsx'
import {
  buildDivergenceSteps,
  divergenceCounterText,
  divergenceRailTone,
  entriesWithoutDivergence,
  nextOpenDivergence,
  placeAfterSwitch,
} from '../lib/divergenceSteps.ts'
import { useDivergenceOnly } from '../lib/useDivergenceOnly.ts'
import { DivergenceOnlyNotice, DivergenceOnlyToggle } from '../components/DivergenceOnly.tsx'
import { DivergenceCard } from '../components/DivergenceCard.tsx'
import { DivergenceContext, type ContextPreparation } from '../components/DivergenceContext.tsx'

/**
 * Conferring an verification: one entry at a time, with the prescription beside it.
 *
 * **Every entry, including the clean ones.** V2's version showed only findings
 * still awaiting a decision, to spare a pharmacist the ones that agreed. V1
 * walks all of them on purpose, and its reason is not a preference: pharmacists
 * are legally required to review every item, so a pass that silently skips
 * items cannot be the record that they were reviewed -- and being that record
 * is what this screen is for.
 *
 * A step is an *entry*, never a single finding. A preparation is reviewed as a
 * preparation, with its form, ativos, posologia and volume in front of you at
 * once. The rail above says where you are, what is flagged and how much is
 * left; the viewer frames the step's blocks as you arrive on it.
 */

/** Statuses after which nothing more is going to happen on its own. */
const ENDED = new Set(['failed', 'cancelled', 'verified', 'released', 'discarded'])

export interface GuidedReviewProps {
  readonly id: string
  readonly onExit: (finished: boolean) => void
  /** Where to go with the next conferência, once this one is concluded. */
  readonly onNext?: ((id: string) => void) | undefined
  /** Operator controls appear only for an operator. */
  readonly isSuperAdmin?: boolean | undefined
  /**
   * Follows a link to a conferência in another account (#586): a copy is a
   * simulation in the internal account, and its original is a pharmacy's.
   */
  readonly onOpenInAccount?:
    | ((
        accountId: string,
        verificationId: string,
        event: ReactMouseEvent<HTMLAnchorElement>,
      ) => void)
    | undefined
  /**
   * The statuses this person's role sees (#439), undefined until the session
   * says. A conferência moving into one they do not see leaves their reach:
   * sent for correction by an auditor, or sent back by a corrector.
   */
  readonly visibleStatuses?: readonly string[] | undefined
  /**
   * Where the account's stages start on its FC12540 (#673), undefined until
   * the session says. The stage badge waits for them rather than guessing.
   */
  readonly stagePositions?: StagePositions | undefined
  /** Back to the golden-set case this was run from, when it was run from one. */
  readonly onOpenCase?: ((caseId: string) => void) | undefined
  /** The same, for the comparison set: a different screen, a different case. */
  readonly onOpenResolutionCase?: ((caseId: string) => void) | undefined
  /**
   * A finding to open on, by fingerprint: its step, its row pinned and in view.
   * From a link that shows one case -- a feedback in the acompanhamento.
   */
  readonly focusFinding?: string | undefined
  /** Feedback triage owns its context and navigation outside the comparison workspace. */
  readonly feedbackReview?: boolean | undefined
  /** Where that finding was, for when a re-run has changed it. */
  readonly focusNear?:
    { readonly preparation?: string | undefined; readonly check?: string | undefined } | undefined
}

/** One hint, one key. Named for what it explains, not where it appears. */
const COACH_KEY = 'decision-actions'

/** A key, drawn as one: a small cap in the product's own tones. */
const KBD =
  'border-stroke-weak bg-fill-weak text-text-strong inline-block rounded-[4px] border px-1 text-[11px] leading-4 font-medium'

export function GuidedReview({
  id,
  onExit,
  onNext,
  onOpenCase,
  onOpenResolutionCase,
  isSuperAdmin,
  onOpenInAccount,
  visibleStatuses,
  stagePositions,
  focusFinding,
  feedbackReview,
  focusNear,
}: GuidedReviewProps) {
  const [detail, setDetail] = useState<Detail | undefined>()
  /*
   * Declared here with the others, and never below an early return.
   *
   * This screen returns early for the loading, failed and waiting statuses, so a
   * hook added further down runs on some renders and not others -- React then
   * matches hook 26 against nothing and the page stops rendering entirely.
   * Every hook in this ingredient belongs above the first `return`.
   */
  const [showingHistory, setShowingHistory] = useState(false)
  const [choosingGoldenSet, setChoosingGoldenSet] = useState(false)
  const copyToSimulation = useCopyToSimulation(id)
  /** The leitor's own object, for an operator checking how the receita was read. */
  const [showingReading, setShowingReading] = useState(false)
  /*
   * The hint shown when somebody goes back for what they left unanswered.
   *
   * They arrive at a pending difference and what they now have to do is two
   * small buttons on one row of a dense screen. It reappears every time until
   * they say not to: learning where a control lives takes more than one
   * encounter, and a hint that vanishes after being seen once is one most
   * people see while thinking about something else.
   */
  const [coaching, setCoaching] = useState(false)
  /*
   * The step, remembered per verification for this tab.
   *
   * Leaving for the queue and coming back lands on the step somebody was
   * on; a different verification starts from the first. `sessionStorage`, so it
   * is this tab's memory of this visit and not a preference: a new tab, or
   * tomorrow, starts fresh.
   */
  const [index, setIndexStatus] = useState(() => rememberedStep(id))
  const setIndex = (next: number | ((current: number) => number)) =>
    setIndexStatus((current) => {
      const resolved = typeof next === 'function' ? next(current) : next
      rememberStep(id, resolved)
      return resolved
    })
  const [visited, setVisited] = useState<ReadonlySet<number>>(() => new Set([rememberedStep(id)]))
  /*
   * Which row is being shown on the prescription.
   *
   * A step's regions drawn together overlap: a preparation's ingredients sit
   * on consecutive lines of one paragraph, so the boxes stack and the frame
   * has to zoom out to hold all of them. Choosing one row puts a rectangle
   * around the thing being read, and frames that.
   *
   * V1 has nothing like this -- its highlight is always the whole step. This
   * is finer because V2's citations are per field rather than per item, which
   * is a capability worth spending.
   */
  /*
   * Every pinned row, not one.
   *
   * A comparison is often two rows read together -- an ingredient and the
   * volume it fills -- and one selection at a time made that a matter of
   * remembering where the last one was. The viewer frames the union of
   * whatever it is given, so several pins zoom to cover all of them.
   */
  const [pinned, setPinned] = useState<
    ReadonlyMap<string, { regions: readonly string[]; tone: 'error' | 'brand' }>
  >(new Map())
  /*
   * What the pointer is over, which is not what is chosen.
   *
   * Kept apart from `located` so a preview never disturbs a pin: glancing at
   * a neighbouring row shows it on the page, and moving away puts the pinned
   * one back. Merging the two meant every glance either cost a click or lost
   * the row you had chosen.
   */
  const [preview, setPreview] = useState<
    | {
        /** The row it belongs to, so the comparison side can mark it too. */
        key: string
        regions: readonly string[]
        tone: 'error' | 'brand'
        /**
         * Which side the pointer is on.
         *
         * Only a row hover moves the viewport. Pointing at the prescription
         * must not: the reader is looking at the page, and zooming it under
         * their pointer moves the very thing they are reading. Clicking is
         * how they ask for that, and clicking pins -- which ends the reverse
         * hover anyway, since it only runs while nothing is pinned.
         */
        from: 'row' | 'block'
      }
    | undefined
  >()
  /** Whether the reader has pinned or unpinned anything on this step. */
  const [touched, setTouched] = useState(false)
  /*
   * `busy` means a reverify or a sign-off is running, and nothing else.
   *
   * Recording a decision used to set it too, so answering a difference made the
   * "Atualizar comparação" button read "Atualizando…" -- announcing a
   * re-comparison that was not happening, on the one control whose whole job
   * is to say when one is.
   */
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  /*
   * The pins, flattened for the viewer.
   *
   * Undefined rather than an empty array when nothing is pinned, so the step's
   * own regions still show: an empty selection means "nothing chosen", not
   * "show nothing".
   */
  const knownProducts = useMemo(() => new Set(detail?.knownProducts ?? []), [detail?.knownProducts])
  const pinnedKeys = useMemo(() => new Set(pinned.keys()), [pinned])
  /*
   * Undefined only before anybody has chosen; empty once they have.
   *
   * Falling back to the step's own regions whenever the pin set was empty
   * meant unpinning the last row put every rectangle back -- including the one
   * just removed, which looked like the unpin had failed on the page while
   * working in the table. A step opens showing what it is about; clearing the
   * pins is a decision, and it should be obeyed.
   */
  const pinnedRegions = useMemo(
    () =>
      pinned.size === 0 && !touched ? undefined : [...pinned.values()].flatMap((p) => p.regions),
    [pinned, touched],
  )
  /*
   * Raised when somebody concludes with findings still unanswered.
   *
   * A confirmation rather than a block: a pharmacist may have a good reason to
   * leave something open, and a screen that refuses to let them finish is one
   * they will work around. What it must not do is let them finish *without
   * noticing*, because the completed pass is the record that every item was
   * reviewed.
   */
  const [confirmingExit, setConfirmingExit] = useState(false)
  useEffect(() => {
    // The warning is the next thing to answer, so its first button takes
    // the focus. Tab moves between the two answers; Enter gives one.
    if (confirmingExit) reviewPendingRef.current?.focus()
  }, [confirmingExit])
  const [error, setError] = useState<string | undefined>()

  async function load() {
    try {
      setDetail(await api.verification(id))
      setError(undefined)
    } catch (cause) {
      /*
       * A 404 on a conferência that was open a moment ago means it moved out
       * of what this person's role sees (#439) -- the server answers the same
       * as for one that does not exist. Said as that, not as "not found".
       */
      setError(
        cause instanceof RequestError && cause.status === 404
          ? 'Esta conferência não está mais entre as que você pode ver.'
          : cause instanceof Error
            ? cause.message
            : 'falha ao carregar a conferência',
      )
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const decisions = useMemo(
    () => new Map((detail?.decisions ?? []).map((r) => [r.fingerprint, r])),
    [detail],
  )
  const prescription = useMemo(() => parseSide(detail?.verification.prescription), [detail])
  /*
   * The keyed side, parsed the same way as the read side.
   *
   * The steps are built from it, and a sem-receita conferência draws its
   * boards from it: everywhere else the two sides are drawn from the
   * findings, which compare them. With no receita there is nothing to
   * compare, and the quotation is the only side there is.
   */
  const quotation = useMemo(() => parseSide(detail?.verification.quotation), [detail])
  // The ficha de produção stored beside it, for the section under each item.
  const storedSheet = useMemo(() => parseProductionSheet(detail?.verification.quotation), [detail])
  /*
   * Read from the ERP when none was stored: the ficha is stored only since
   * 2026-09-23, and a conferência read before then can still be open
   * (6/836158). Asked once; a failure leaves the section out, as it was.
   */
  const [liveSheet, setLiveSheet] = useState<ReturnType<typeof parseProductionSheet>>()
  const askedSheet = useRef(false)
  useEffect(() => {
    if (askedSheet.current || detail === undefined || storedSheet !== undefined) return
    if (detail.verification.quotation == null) return
    askedSheet.current = true
    void api
      .liveProductionSheet(id)
      .then((r) => setLiveSheet(parseProductionSheet({ productionSheet: r.sheet })))
      .catch(() => undefined)
  }, [id, detail, storedSheet])
  const productionSheet = storedSheet ?? liveSheet
  const allSteps = useMemo(
    () => buildGuidedSteps(prescription, quotation, detail?.findings ?? []),
    [prescription, quotation, detail],
  )
  /*
   * Comparação or Correção (#439).
   *
   * Waiting on a correction, whoever opens it came to fix what the
   * pharmacist marked *Corrigir no sistema* -- so it opens on those steps
   * alone, and the full comparison is one click away. Everywhere else, and
   * when nothing is marked, there is only the comparison.
   */
  const toCorrect = useMemo(
    () =>
      detail?.verification.status === 'awaiting_correction'
        ? correctionSteps(allSteps, detail.decisions)
        : [],
    [allSteps, detail],
  )
  const [view, setView] = useState(ReviewView.Correction)
  const offersCorrectionView = toCorrect.length > 0
  /*
   * The adversarial review (#541): a second opinion on how we read and judged
   * this conferência, for a super admin. Its result opens in a modal, and can
   * be walked here -- this same page, narrowed to the steps it disputes and
   * read-only. The walk is a third list of steps beside the two below, and
   * while it is on, nothing on this screen answers, concludes or claims.
   */
  const reviewedFindings = useMemo(
    () => (detail?.findings ?? []).map((f) => f.fingerprint).join('|'),
    [detail?.findings],
  )
  const review = useAdversarialReview(id, isSuperAdmin === true, reviewedFindings)
  const walk = useReviewWalk(review.run, allSteps)
  const [showingReviewResult, setShowingReviewResult] = useState(false)
  /*
   * The guided tour over the walk: one balloon per disagreement, pointing at
   * its row. `tourAt` is which stop is on screen; the step under it follows.
   */
  const tour = useMemo(() => reviewTour(walk.disputed), [walk.disputed])
  const [tourAt, setTourAt] = useState(0)
  // Held while this screen is open. Somebody else's conferência still opens --
  // reading is never undecided -- but the queue stops offering it as next work.
  // Let go for as long as the review is being walked: that is reading how we
  // judged, not conferring, and must not keep the conferência from anybody.
  // Reading a feedback must not hold the pharmacy's operational conference lease.
  const claim = useConferenceClaim(id, !walk.walking && feedbackReview !== true)
  // A pote (#558) or a label (#614) the ERP wrote after this was read: said,
  // with what to do about it a press away.
  useLateErp(
    id,
    () => void reverify(),
    () => void load(),
  )
  const showingCorrections = offersCorrectionView && view === ReviewView.Correction && !walk.walking
  /*
   * "Somente divergências" (#641): one step per finding to answer, the person's
   * choice. Not where a pass of its own is already on screen -- Correção, the
   * review's walk -- nor without a prescription, nor when a link opens one finding,
   * which may well be one that is not a divergence. With nothing to answer the
   * full pass stays, under a line that says so: an empty step would be a
   * verification nobody could conclude.
   */
  const [divergencePreference, setDivergenceOnly] = useDivergenceOnly()
  const divergenceSteps = useMemo(() => buildDivergenceSteps(allSteps), [allSteps])
  const divergenceAllowed =
    detail?.verification.status !== 'discarded' &&
    feedbackReview !== true &&
    !namesFinding(focusFinding, focusNear)
  const divergenceOffered = divergenceAllowed && !walk.walking && !showingCorrections
  const divergenceOnly = divergencePreference && divergenceOffered
  const walkingDivergences = divergenceOnly && divergenceSteps.length > 0
  /** The comparison's own pass, outside Correção: every entry, or the divergences. */
  const comparisonSteps =
    divergencePreference && divergenceAllowed && divergenceSteps.length > 0
      ? divergenceSteps
      : allSteps
  const steps = walk.walking ? walk.steps : showingCorrections ? toCorrect : comparisonSteps
  /*
   * Switching passes keeps the place: the first divergence of the entry
   * somebody was on, or the entry a divergence belongs to. Only between the
   * two comparison passes -- the walk and Correção set their own place.
   */
  const lastPass = useRef({
    divergences: walkingDivergences,
    offered: divergenceOffered,
    steps,
    index,
  })
  useEffect(() => {
    const before = lastPass.current
    if (before.divergences === walkingDivergences || !before.offered || !divergenceOffered) return
    const at = placeAfterSwitch(steps, before.steps[before.index])
    setVisited(new Set([at]))
    setIndex(at)
    setPinned(steps[at] === undefined ? new Map() : openingPinMap(steps[at]))
    setTouched(false)
    // Only on the switch itself; the pass and the place are read from the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkingDivergences, divergenceOffered])
  // After the switch above has read it: what the last render showed.
  useEffect(() => {
    lastPass.current = { divergences: walkingDivergences, offered: divergenceOffered, steps, index }
  }, [walkingDivergences, divergenceOffered, steps, index])
  /*
   * In Correção, only the rows that were marked: the rest of the step is the
   * comparison, one tab away. What was asked to fix, and nothing to read past.
   */
  const marked = useMemo(
    () =>
      new Set(
        (detail?.decisions ?? [])
          .filter((d) => d.decision === 'corrected')
          .map((d) => d.fingerprint),
      ),
    [detail],
  )

  /**
   * Starts a review with its modal open: the wait is shown where the result
   * will be, and closing it leaves the run going.
   */
  function runReview() {
    setShowingReviewResult(true)
    void review.start()
  }

  /** Into the tour, on the first stop of one of the disputed steps. */
  function walkReview(stepAt = 0) {
    if (walk.disputed[stepAt] === undefined) return
    setShowingReviewResult(false)
    walk.begin()
    setVisited(new Set())
    goToStop(firstStopOn(tour, stepAt))
  }

  /** To one stop of the tour: its step on screen, its row pinned. */
  function goToStop(at: number) {
    const stop = tour[at]
    if (stop === undefined) return
    setTourAt(at)
    setVisited((seen) => new Set([...seen, stop.stepAt]))
    setIndex(stop.stepAt)
    setPinned(pinMap(disagreementPins(stop.step, stop.disagreement)))
    setTouched(false)
  }

  /** Back to the conferência as it is, from its first step. */
  function leaveReview() {
    walk.end()
    const back =
      offersCorrectionView && view === ReviewView.Correction ? toCorrect : comparisonSteps
    setVisited(new Set([0]))
    setIndex(0)
    setPinned(back[0] === undefined ? new Map() : openingPinMap(back[0]))
    setTouched(false)
  }

  /** From the result to the step a disagreement is about, in the walk. */
  function jumpToDisagreement(stepIndex: number) {
    const at = walk.disputed.findIndex((one) => one.index === stepIndex)
    walkReview(at === -1 ? 0 : at)
  }

  function switchView(next: ReviewView) {
    if (next === view) return
    setView(next)
    // The steps are a different list, so the place in it starts over.
    setVisited(new Set([0]))
    setIndex(0)
    setPinned(openingPinMap((next === ReviewView.Correction ? toCorrect : comparisonSteps)[0]!))
    setTouched(false)
  }

  /*
   * Rótulos read again on opening, when none was stored (Galdino, 2026-09-29).
   *
   * The ERP writes a rótulo some time after the requisição is keyed, and the
   * conferência is read as soon as it is, so a third of those stored without
   * one had one by the time anybody looked. Asked once per conferência, and
   * only when no preparation carries a rótulo; a failure leaves the panel
   * empty, as it was.
   */
  const [liveLabels, setLiveLabels] = useState<ReadonlyMap<string, readonly string[]>>(new Map())
  const askedLabels = useRef(false)
  useEffect(() => {
    if (askedLabels.current || quotation === undefined || quotation.preparations.length === 0)
      return
    if (quotation.preparations.some((p) => p.label !== undefined)) return
    askedLabels.current = true
    void api
      .liveLabels(id)
      .then((r) =>
        setLiveLabels(
          new Map(
            r.labels.flatMap((one) =>
              Array.isArray(one.label?.lines) ? [[one.preparationId, one.label.lines]] : [],
            ),
          ),
        ),
      )
      .catch(() => undefined)
  }, [id, quotation])
  const labelLinesOf = (preparation: typeof currentPreparation) =>
    preparation?.label?.lines ??
    (preparation === undefined ? undefined : liveLabels.get(preparation.id))

  const current: GuidedStep | undefined = steps[index]
  const currentPreparation = quotation?.preparations.find(
    (f) => f.id === current?.item?.preparationId,
  )
  // A remembered step past the end -- the findings changed since -- falls
  // back to the last one rather than to an empty screen.
  useEffect(() => {
    if (steps.length > 0 && index > steps.length - 1) setIndex(steps.length - 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length, index])

  /*
   * Every row of this step, by the blocks it cites.
   *
   * Built once per step so pointing at the prescription can answer "what did
   * the system make of this line" without walking the findings on every
   * mouse move.
   */
  const rowsByBlock = useMemo(() => {
    const index = new Map<
      string,
      { key: string; regions: readonly string[]; tone: 'error' | 'brand' }
    >()
    if (current === undefined) return index
    const add = (key: string, regions: readonly string[], tone: 'error' | 'brand') => {
      for (const ref of regions) if (!index.has(ref)) index.set(ref, { key, regions, tone })
    }

    /*
     * Ingredient rows first, then the checks.
     *
     * A block is often cited by both -- `composition` cites every ingredient
     * it compared, and each ingredient row cites its own name. The first
     * writer wins here, and it used to be the finding, while the step opens
     * with the *row* pinned. So clicking that block pinned a second thing
     * over the first instead of removing it: two rectangles stacked, and the
     * one you could see was not the one you could unpin.
     *
     * Rows win because they are what the reader is looking at. A row is one
     * ingredient; a composition finding is the whole list.
     */
    if (current.item !== undefined) {
      for (const row of activesBoard(current.item).rows) {
        add(row.key, row.regions, row.status === 'error' ? 'error' : 'brand')
      }
    }
    for (const finding of current.findings) {
      add(
        finding.fingerprint,
        finding.regions,
        finding.disposition === 'divergence' ? 'error' : 'brand',
      )
    }
    return index
  }, [current])

  /*
   * What a pinned row concludes *now*.
   *
   * A pin records the tone it had when it was set, and a decision given a
   * moment later left the rectangle red until the row was re-pinned. The
   * tone is read from the row's findings against what has been answered,
   * so the page changes colour the moment the table does.
   */
  const liveTone = (key: string, stored: 'error' | 'brand'): 'error' | 'brand' => {
    if (current === undefined) return stored
    const answered = new Set(decisions.keys())
    const row =
      current.item === undefined
        ? undefined
        : activesBoard(current.item).rows.find((r) => r.key === key)
    if (row !== undefined)
      return worstStatus(row.findings, answered) === 'error' ? 'error' : 'brand'
    const finding = current.findings.find((f) => f.fingerprint === key)
    if (finding !== undefined) {
      return columnStatusFor(finding.disposition, answered.has(key)) === 'error' ? 'error' : 'brand'
    }
    return stored
  }
  // Any flagged row makes the whole selection wear the alarm colour: a red
  // rectangle among blue ones would say the others were fine to ignore.
  const errorRegions = useMemo(
    () => [
      ...[...pinned.entries()]
        .filter(([key, p]) => liveTone(key, p.tone) === 'error')
        .flatMap(([, p]) => p.regions),
      ...(preview?.tone === 'error' ? (preview.regions ?? []) : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinned, preview, decisions, current],
  )
  const pinnedTone = useMemo(
    () =>
      pinned.size === 0
        ? undefined
        : [...pinned.entries()].some(([key, p]) => liveTone(key, p.tone) === 'error')
          ? ('error' as const)
          : ('brand' as const),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinned, decisions, current],
  )

  /*
   * Opening on one finding, once, when the link names one.
   *
   * Pinned through a ref rather than directly, because moving to the step
   * re-runs the effect below, which would pin the step's own opening row over
   * it. Only the first time the steps arrive: after that the reader walks
   * wherever they like.
   */
  const focusing = useRef<
    { stepId: string; pins: ReturnType<typeof openingPinMap>; key: string } | undefined
  >(undefined)
  const focusHandled = useRef(false)
  useEffect(() => {
    // Not on the first steps: "Dados cadastrais" exists before any finding
    // has arrived, and judging the link against that would always miss.
    if (focusHandled.current || detail === undefined) return
    if (!namesFinding(focusFinding, focusNear)) return
    focusHandled.current = true
    const opening = openingForFinding(steps, focusFinding, focusNear)
    if (opening === undefined) {
      toast.show('Este apontamento não está mais nesta conferência', { tone: 'info' })
      return
    }
    if (!opening.exact) {
      // The finding was re-raised differently since the feedback: same pote,
      // same kind of check, but not the same words.
      toast.show('O apontamento mudou desde o feedback', {
        tone: 'info',
        detail: 'Abrindo no mesmo item da conferência.',
      })
    }
    if (opening.pins.length === 0) {
      setVisited((seen) => new Set([...seen, opening.index]))
      setIndex(opening.index)
      return
    }
    const pins = new Map(
      opening.pins.map((row) => [row.key, { regions: row.regions, tone: row.tone }]),
    )
    focusing.current = { stepId: steps[opening.index]!.id, pins, key: opening.pins[0]!.key }
    setVisited((seen) => new Set([...seen, opening.index]))
    setIndex(opening.index)
    setPinned(pins)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusFinding, focusNear, detail, steps.length])

  useEffect(() => {
    // The first step, and every reload: `goTo` covers the ones you walk to,
    // this covers the one you land on -- unless a link named the finding.
    const focus = focusing.current
    if (current !== undefined && focus !== undefined && focus.stepId === current.id) {
      setPinned(focus.pins)
      setTouched(true)
      // Into view once the row has rendered; the panel scrolls, not the page.
      let tries = 0
      const scroll = () => {
        const row = document.querySelector(`[data-review-row="${CSS.escape(focus.key)}"]`)
        if (row !== null) row.scrollIntoView({ block: 'center' })
        else if (++tries < 30) requestAnimationFrame(scroll)
      }
      requestAnimationFrame(scroll)
      // Again once the landing has settled: the keyboard pass focuses the
      // first row still to answer, and focusing scrolls it into view.
      setTimeout(() => {
        tries = 0
        scroll()
      }, 600)
      focusing.current = undefined
      return
    }
    if (current !== undefined) {
      setPinned(openingPinMap(current))
      setTouched(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.findings.length])

  /**
   * Where the analysis has got to, surviving the workflow itself.
   *
   * Read from the step rather than from an empty findings list, because
   * "nothing found yet" and "nothing wrong" render identically and mean
   * opposite things.
   */
  /*
   * The live view is fresher than the row *while the verification is live*, and not
   * after. A terminal row wins outright: an ended verification's last checkpoint is
   * not news, and treating it as such is how a failed run rendered as a
   * spinner still reading the prescription.
   */
  const settled = detail !== undefined && ENDED.has(detail.verification.status)
  const step = settled
    ? detail.verification.status
    : (detail?.live?.step ?? detail?.verification.step ?? detail?.verification.status ?? '')

  /*
   * Whether a run is happening is a question only a *status* can answer.
   *
   * This was being asked of `step`, and the two stopped sharing a vocabulary
   * when they were split apart: a checkpoint says how far into a run something
   * is, a status says whether a run is happening at all. So while an verification was
   * actually working -- `live.step` of `scoring`, `resolving`, `detecting` --
   * `isAnalysing` was handed a word it has never heard of and answered no.
   *
   * The progress strip then disappeared for exactly the minutes it exists to
   * cover, and an verification resolving ingredients at twelve seconds each looked
   * indistinguishable from one that had died. Which is how a healthy run got
   * reported as stuck.
   */
  const status = settled
    ? detail.verification.status
    : (detail?.live?.status ?? detail?.verification.status ?? '')
  /*
   * Whether the analysis is in flight, from the workflow where it can be asked.
   *
   * The same question the queue asks, so both screens answer it identically —
   * and now also what decides whether a refresh may be requested at all. `busy`
   * covered only the moment the request was in flight, so the button came back
   * the instant the signal was accepted and stayed pressable for the minutes
   * the analysis actually takes. Every extra press is a full re-read and
   * another round of model calls, bought for an answer already on its way.
   */
  const analysing = isAnalysing(status)
  /*
   * Nothing may be answered any more.
   *
   * Concluded is concluded: a decision changed afterwards would be a record
   * changed after it was signed. Waiting on a correction is a different
   * lock: the quotation is being changed in the ERP, and the thing to do is
   * look again -- "atualizar comparação" -- not re-answer findings about a
   * quotation that no longer exists.
   */
  const awaitingCorrection = status === 'awaiting_correction'
  const locked = awaitingCorrection || ENDED.has(status)
  const manual = useManualAttachments(id, () => void load())
  // Which document the viewer has on screen, for the header's manual mark.
  const [shownAttachment, setShownAttachment] = useState<string | undefined>()
  const { pages: listedPages, status: pagesStatus } = usePrescriptionPages(
    id,
    analysing,
    manual.revision,
  )
  // A removed receita leaves the screen now, and an attached one shows now,
  // not when the run that follows ends.
  const pageUrls = useMemo(
    () =>
      shownPages(listedPages, {
        attachments: manual.attachments,
        loaded: manual.loaded,
        previews: manual.previews,
      }),
    [listedPages, manual.attachments, manual.loaded, manual.previews],
  )
  // Nothing to attach to once the requisição is made, and nothing to attach
  // mid-run: the run under way has already listed what it will read.
  const attachDisabled = analysing || status === 'released'

  /*
   * A simulation is watched from the moment it is dispatched: somebody uploads
   * a prescription and stays on this screen. Without this they would sit on
   * a finished verification's checkpoints until they reloaded by hand.
   *
   * The checkpoint arrives on its own; the whole detail is read once, when the
   * analysis stops and the findings it was waiting for exist.
   */
  useVerificationProgress(
    id,
    analysing,
    (progress) =>
      setDetail((current) => (current === undefined ? current : withProgress(current, progress))),
    () => void load(),
  )

  function goTo(next: number) {
    const clamped = Math.max(0, Math.min(next, steps.length - 1))
    setVisited((seen) => new Set([...seen, index, clamped]))
    setIndex(clamped)
    // A new step is a new question; carrying the last row's highlight into it
    // would point at the previous preparation. It opens on its own divergence
    // when it has exactly one.
    if (walk.walking) {
      // In the tour the rail moves between disputed steps, and the balloon
      // follows to that step's first stop.
      goToStop(firstStopOn(tour, clamped))
      return
    }
    setPinned(openingPinMap(steps[clamped]!))
    setTouched(false)
  }

  /*
   * The keyboard pass.
   *
   * Galdino's spec, for the "experts" he wants to create: ← → move between
   * steps and ↑ ↓ between apontamentos -- the mapping the screen already
   * draws, a horizontal strip of steps above a vertical list of rows. Tab does
   * what ↓ does. Both stop on every question, answered or not, so an answer
   * can be gone back to; past a step's last they move on -- to the next
   * step's first, or on the last step to "Concluir", which only Enter fires.
   * ← → land where the work is: the step's first question still open, or its
   * button when none is. A or Enter on a row is "corrigir"; X, Backspace or
   * Delete is "não é divergência", whose question is itself Tab-walkable and
   * answered by Enter; Z takes an answer back. Escape leaves rather than
   * answers: it clears the pins. Where the focus is must always be visible,
   * which the rows' own ring does.
   *
   * Delegated from the panel rather than wired into every row: the rows know
   * how to pin and preview themselves, and only this screen knows what the
   * next step is.
   */
  const panelRef = useRef<HTMLElement>(null)
  const concludeRef = useRef<HTMLButtonElement>(null)
  const goToRef = useRef<HTMLInputElement>(null)
  /** Where the focus goes once a step change has rendered. */
  const pendingFocus = useRef<StepFocus | undefined>(undefined)
  /**
   * Where to go once an answer has landed: the row that now holds the
   * answered one's place in the list, or the footer button when it was the
   * last. Set when a key answers a row, read when the decisions change.
   */
  const advanceTo = useRef<number | undefined>(undefined)
  /** "Revisar pendências", focused the moment the warning appears. */
  const reviewPendingRef = useRef<HTMLButtonElement>(null)
  const leaveAsIsRef = useRef<HTMLButtonElement>(null)

  /** Back into the pass: where the work is on the current step. */
  const focusFirstRow = () => {
    // Over a few frames: when this follows a step change the new step's rows
    // are not always in the tree on the first one.
    let tries = 0
    const attempt = () => {
      const target = landing()
      target?.focus()
      if (document.activeElement === target || ++tries >= 10) return
      requestAnimationFrame(attempt)
    }
    requestAnimationFrame(attempt)
  }

  /*
   * The questions on the step, answered or not: where ↑ ↓ and Tab stop.
   *
   * Only the open ones used to be stops, so an answer could be taken back by
   * mouse only, and on the divergence-only pass the focus fell past an
   * answered card onto the footer's button, where the next Space moved the
   * pass on under somebody who had only come back to look.
   */
  const stops = () =>
    panelRef.current === null
      ? []
      : [...panelRef.current.querySelectorAll<HTMLElement>('[data-question="true"]')]
  /** The questions still to answer. */
  const openStops = () => stops().filter((one) => one.dataset['needsDecision'] === 'true')
  /*
   * Where the work is on a step: its first question still open, or its
   * button when none is, so Enter moves on and never answers by accident. On
   * the divergence-only pass the card, answered or not: it is the whole step.
   */
  const landing = () =>
    (walkingDivergences ? stops()[0] : openStops()[0]) ?? concludeRef.current ?? undefined

  useEffect(() => {
    const want = pendingFocus.current
    if (want === undefined) return
    pendingFocus.current = undefined
    const all = stops()
    const target =
      want === 'land'
        ? landing()
        : want === 'first'
          ? all[0]
          : want === 'last'
            ? all[all.length - 1]
            : undefined
    // A step with nothing to stop on lands on its own button: "próximo item",
    // or "concluir" at the end. Enter takes it from there.
    ;(target ?? concludeRef.current)?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, steps])

  /*
   * On arrival, the first thing to answer.
   *
   * The browser's own first Tab went to the logo, and a pharmacist who had
   * just opened a conferência was four presses from the first divergence.
   * The screen starts where the work is: the first row still to answer on
   * the first step, or that step's button if it has none.
   */
  const landed = useRef(false)
  useEffect(() => {
    if (landed.current || steps.length === 0) return
    /*
     * Tried over a few frames, not once.
     *
     * One frame after the steps arrive the panel is often still filling in
     * -- the claim answers, the image lands, a toast mounts -- and a focus
     * set into that was being lost, which left the first Tab to do the
     * landing and the second to move. Kept trying until something in the
     * panel actually holds the focus, then never again.
     */
    let tries = 0
    let frame = 0
    const attempt = () => {
      landing()?.focus()
      const held = panelRef.current?.contains(document.activeElement) === true
      if (held) {
        landed.current = true
        /*
         * And held on to. Coming in from the queue, the focus landed and was
         * then dropped to the body by something mounting after it -- the
         * claim's answer, the image -- where a reload had nothing left to
         * mount. For a moment after landing, a focus that falls to the body
         * is put back; a focus somebody moved on purpose is left alone.
         */
        const guard = setInterval(() => {
          if (document.activeElement === document.body) landing()?.focus()
        }, 100)
        // And off the moment a person touches anything: a guard that keeps
        // moving the focus under a click is worse than the drop it guards
        // against.
        const stop = () => {
          clearInterval(guard)
          window.removeEventListener('pointerdown', stop, true)
          window.removeEventListener('keydown', stop, true)
        }
        window.addEventListener('pointerdown', stop, true)
        window.addEventListener('keydown', stop, true)
        setTimeout(stop, 2000)
        return
      }
      if (++tries < 30) frame = requestAnimationFrame(attempt)
    }
    frame = requestAnimationFrame(attempt)
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length, detail])

  useEffect(() => {
    const at = advanceTo.current
    if (at === undefined) return
    advanceTo.current = undefined
    /*
     * On the divergence-only pass the next row to answer is on another step:
     * the next divergence still open. With none open, the last step, where
     * the footer's button concludes.
     */
    if (walkingDivergences) {
      const next = nextOpenDivergence(steps, index, new Set(decisions.keys()))
      const to = next ?? steps.length - 1
      if (to === index) {
        concludeRef.current?.focus()
        return
      }
      pendingFocus.current = next === undefined ? 'conclude' : 'land'
      goTo(to)
      return
    }
    const rows = openStops()
    // The answered row has left the open ones; whatever took its index is next.
    const target = rows[Math.min(at, rows.length - 1)]
    if (target !== undefined) target.focus()
    else concludeRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions])

  function onPanelKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    const row = (event.target as HTMLElement).closest<HTMLElement>('[data-review-row]')
    if (row === null) return
    const press = (decision: 'fix' | 'dismiss') => {
      event.preventDefault()
      // Remember where this row stood, so the answer can hand focus on.
      advanceTo.current = Math.max(0, openStops().indexOf(row))
      row.querySelector<HTMLButtonElement>(`[data-decision="${decision}"]`)?.click()
    }
    /*
     * Up and Down walk the rows; Left and Right walk the steps.
     *
     * Galdino, reading the body rather than the strip above it: "quando
     * navego, estou lá em cima nas etapas, não no corpo da comparação". The
     * arrows were all horizontal, so the only key that moved between
     * apontamentos was Tab -- a key about focus order, not about place. The
     * mapping is now the one the screen already draws: the steps are a
     * horizontal strip, the apontamentos are a vertical list.
     */
    const intent = rowIntent(
      event.key,
      event.shiftKey,
      event.ctrlKey || event.metaKey || event.altKey,
    )
    if (intent === 'next-row' || intent === 'previous-row') {
      const backwards = intent === 'previous-row'
      const rows = stops()
      const at = rows.indexOf(row)
      const next = backwards ? rows[at - 1] : rows[at + 1]
      if (next !== undefined) {
        event.preventDefault()
        next.focus()
        return
      }
      if (backwards) {
        if (index === 0) return
        event.preventDefault()
        pendingFocus.current = 'last'
        goTo(index - 1)
        return
      }
      event.preventDefault()
      if (index === steps.length - 1) {
        concludeRef.current?.focus()
        return
      }
      pendingFocus.current = 'first'
      goTo(index + 1)
      return
    }
    /*
     * Z takes the answer back, on whatever row holds one: the same as
     * pressing its answered button, which is the undo by mouse.
     */
    if (intent === 'undo') {
      const undo = row.querySelector<HTMLButtonElement>('[data-decision="undo"]')
      if (undo === null || undo.disabled) return
      event.preventDefault()
      undo.click()
      return
    }
    if (row.dataset['needsDecision'] !== 'true') return
    if (intent === 'fix' || intent === 'dismiss') press(intent)
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      // Not while typing, and not inside the reason question.
      if (target?.closest('input, textarea, select, [role="dialog"]') !== null) return
      /*
       * Tab from anywhere outside the pass joins it.
       *
       * Focus starts on the page body after a navigation, and on whatever
       * was last clicked after that -- the logo, the viewer, a filter. The
       * browser's Tab from there walks the chrome. The first Tab pressed
       * anywhere on this screen goes to where the work is on the step;
       * from then on the rows carry it themselves.
       */
      if (event.key === 'Tab' && !event.shiftKey) {
        const inside = panelRef.current?.contains(target) === true
        if (!inside) {
          event.preventDefault()
          landing()?.focus()
        }
        return
      }
      /*
       * Escape clears the pins.
       *
       * It used to answer a difference -- "não é divergência" -- which is the
       * one thing Escape means nowhere else. Every application spends that key
       * on leaving the state you are in, so a pharmacist who had pinned three
       * rows to compare them and wanted out pressed it and recorded a decision
       * instead. X, Backspace and Delete all still answer; this one leaves.
       *
       * Nothing to clear is not nothing to do: it is simply quiet, rather than
       * reaching for the next meaning of the key.
       */
      const intent = screenIntent(event.key, event.ctrlKey || event.metaKey || event.altKey)
      if (intent === 'clear-pins') {
        /*
         * Nothing pinned is not nothing to do: it is simply quiet, rather than
         * reaching for the next meaning of the key.
         */
        setTouched(true)
        setPinned(new Map())
        return
      }
      if (intent === 'go-to-requisition') {
        // Prevented, or the slash that focused the field is typed into it.
        event.preventDefault()
        goToRef.current?.focus()
        return
      }
      /*
       * And the focus goes with the step, to where the work is on it. It used
       * to stay behind on a row of the step just left, which was no longer
       * on screen, so the next A or X answered nothing.
       */
      if (intent === 'previous-step' && index > 0) {
        pendingFocus.current = 'land'
        goTo(index - 1)
      }
      if (intent === 'next-step' && index < steps.length - 1) {
        pendingFocus.current = 'land'
        goTo(index + 1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, steps.length])

  /**
   * Stops a run that is not going to finish, and queues the verification again.
   *
   * Deliberately not folded into `reverify`. A reverify asks a running workflow
   * to go round again; this one exists because the workflow is not listening,
   * and conflating the two would put a termination behind the button every
   * pharmacist presses all day.
   */
  async function abandon() {
    if (walk.walking) return
    setBusy(true)
    try {
      await api.abandon(id)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao parar a análise')
      await load()
    } finally {
      setBusy(false)
    }
  }

  async function reverify() {
    /*
     * The screen says so before the server has answered.
     *
     * A reverify is a signal and a reload, and between the two the row still
     * held its old status -- so the badge sat unchanged for a beat and then
     * moved, which reads as a click that did nothing followed by a flicker,
     * and invites a second press. Saying it locally makes the button and the
     * badge change on the press itself; the reload only confirms it.
     */
    setBusy(true)
    setDetail((current) =>
      current === undefined
        ? current
        : // `acquiring`, not `reverifying`: the step column is the progress
          // strip's vocabulary, and a status in it renders as a raw key.
          {
            ...current,
            verification: { ...current.verification, status: 'reverifying', step: 'acquiring' },
          },
    )
    try {
      await api.reverify(id)
      /*
       * Sent back from a correction (#439) by somebody who cannot watch it be
       * read again: on to the next waiting on a correction, or the list when
       * there is none, since reloading it would be a refusal. Everyone else
       * stays and sees the progress.
       */
      if (awaitingCorrection && leavesOnReverify(visibleStatuses)) {
        await moveOn('awaiting_correction')
        return
      }
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao solicitar reconferência')
      // Put back whatever it really is, rather than leaving the screen
      // claiming a reverify that never started.
      await load()
    } finally {
      setBusy(false)
    }
  }

  /**
   * Overrides the ERP's type for a keyed line -- an excipient -- then looks again.
   *
   * Confirmed first: it is chosen under ✗ like an answer, but it changes every
   * later conferência of the account. The override applies when the quotation
   * is fetched, so the finding it answers only goes away on a fresh analysis --
   * asked for here rather than left to somebody pressing Atualizar (ADR 0014).
   */
  async function markExcipient(
    ingredientId: string,
    productName: string,
    fingerprints: readonly string[],
  ) {
    if (walk.walking) return
    const sure = await confirm({
      title: `Tratar ${productName} como excipiente?`,
      message:
        'Deixa de ser comparado com a receita nas próximas conferências desta farmácia, mesmo quando o ERP o registra como componente. Aqui, responde a este apontamento. Dá para desfazer na página do produto.',
      confirmLabel: 'Tratar como excipiente',
      tone: 'danger',
    })
    if (!sure) return
    setBusy(true)
    try {
      await api.markExcipient(id, ingredientId)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao marcar como excipiente')
      setBusy(false)
      return
    } finally {
      setBusy(false)
    }
    /*
     * Answered here, not re-read.
     *
     * The override applies where the quotation is fetched, so the finding it
     * answers only changes on a fresh analysis -- and this used to ask for one
     * straight away. That is minutes of model calls to hide a single line, it
     * re-rolls every other finding the conferente has already worked through,
     * and when the ERP no longer holds the orçamento the reverify fails and
     * takes a finished conferência with it, unrecoverably.
     *
     * The mark is the reason this is not a divergence, so it is recorded as
     * one. The next analysis of this pharmacy's quotations types the line as
     * the excipient it is, which is what the mark was always for.
     */
    // Every apontamento the row's control stands for: the line is an excipient,
    // which answers all of them and not only the one that leads.
    for (const fingerprint of fingerprints) {
      await record(fingerprint, NOT_A_DIVERGENCE, { reason: 'excipient' })
    }
    toast.show(`${productName} marcado como excipiente.`, { tone: 'success' })
  }

  /**
   * Signs the conferência off.
   *
   * It does not decide what that means. A conferência holding anything marked
   * *Corrigir no sistema* becomes `awaiting_correction`, not `conferido` -- the
   * pharmacist has finished reading and the quotation has not finished
   * changing, and saying otherwise would put a claim in the queue the ERP
   * contradicts. That rule lives in the workflow, where the decisions are.
   */
  async function finish(): Promise<void> {
    // Walking a review is reading. See `walk`.
    if (walk.walking) return
    setBusy(true)
    try {
      await api.finishConference(id)
    } catch (cause) {
      /*
       * The server refuses a conferência with anything unanswered, and this is
       * where that refusal surfaces.
       *
       * Reachable even though the screen guards it: a tab left open while
       * somebody else reverified the quotation is looking at findings that no
       * longer match, and its idea of what is outstanding is stale. Reloading
       * is the honest response -- it shows them what actually remains.
       */
      setError(cause instanceof Error ? cause.message : 'falha ao concluir a conferência')
      await load()
      return
    } finally {
      setBusy(false)
    }
    /*
     * Said once, because the conferência is about to leave this person's
     * queue (#439): when their role does not see "aguardando correção", what they
     * marked *Corrigir no sistema* goes to whoever corrects and comes back
     * for the final check -- it has not vanished.
     */
    if (
      !sees(visibleStatuses, 'awaiting_correction') &&
      detail?.decisions.some((d) => d.decision === 'corrected')
    ) {
      toast.show('Enviada para correção. Volta para a sua fila depois de corrigida.', {
        tone: 'success',
      })
    }
    /*
     * Straight on to the next one, as V1 did.
     *
     * Back to the queue to pick the next row by hand was a trip nobody asked
     * for. The server answers what the queue would put at the top; the queue
     * itself is where to go when there is nothing left.
     */
    await moveOn()
  }

  /** The next conferência the queue would offer, or the queue itself. */
  async function moveOn(status?: 'awaiting_correction'): Promise<void> {
    const next =
      onNext === undefined ? null : (await api.nextVerification(id, status).catch(() => null))?.id
    if (onNext !== undefined && typeof next === 'string' && next !== '') {
      // Handed on, it starts from the first step even if this tab had it
      // open before. Coming back to it from the queue is what resumes.
      forgetStep(next)
      onNext(next)
    } else onExit(true)
  }

  /**
   * Records a decision, on screen first.
   *
   * It used to await the write and then refetch the whole conferência — the
   * verification row, every finding, every review, every OCR block — with the screen
   * disabled throughout. On a list of eight differences that is eight freezes,
   * and answering one felt like it had gone wrong.
   *
   * The decision is a fact the moment the pharmacist chooses it, so it lands in
   * the local state immediately and the request follows. A failure puts it
   * back and says so, which is the only case where anybody has to wait.
   */
  async function record(
    fingerprint: string,
    decision: string,
    options?: {
      synonymTerm?: string | undefined
      notes?: string | undefined
      /** Why, in Galdino's wording. See `REASONS`. */
      reason?: string | undefined
    },
  ) {
    // Walking a review is reading: the card is read-only there, and this is
    // the same promise kept for a key press that reached here anyway.
    if (walk.walking) return
    const previous = detail
    setDetail((current) =>
      current === undefined
        ? current
        : {
            ...current,
            decisions: [
              ...current.decisions.filter((r) => r.fingerprint !== fingerprint),
              {
                fingerprint,
                decision,
                verifier_email: sessionEmail() ?? 'você',
                notes: options?.notes ?? null,
                // The optimistic row mirrors what the server will store: the
                // count reads `misread`, and it means this one reason.
                misread: options?.reason === 'ai-misread',
                created_at: new Date().toISOString(),
              },
            ],
          },
    )

    try {
      const result = await api.recordDecision(id, fingerprint, decision, options)
      if (result.synonym === 'stored') {
        toast.show('Sinônimo guardado. Será proposto para aprovação ao concluir a conferência.', {
          tone: 'success',
        })
      }
    } catch (cause) {
      setDetail(previous)
      setError(cause instanceof Error ? cause.message : 'falha ao registrar')
    }
  }

  /** Withdraws a decision, so a finding somebody answered comes back. */
  async function clearDecision(fingerprint: string) {
    if (walk.walking) return
    const previous = detail
    setDetail((current) =>
      current === undefined
        ? current
        : { ...current, decisions: current.decisions.filter((r) => r.fingerprint !== fingerprint) },
    )

    try {
      await api.clearDecision(id, fingerprint)
    } catch (cause) {
      setDetail(previous)
      setError(cause instanceof Error ? cause.message : 'falha ao desfazer')
    }
  }

  if (error !== undefined && detail === undefined) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={() => onExit(false)}
        >
          Voltar para a fila
        </Button>
        <p className="text-tP text-text-error">{error}</p>
      </div>
    )
  }

  if (detail === undefined) {
    return (
      <p className="text-tP text-text-weak" role="status" aria-live="polite">
        Carregando…
      </p>
    )
  }

  /*
   * Being analysed, with nothing to show yet.
   *
   * The whole screen, because there is no screen yet -- a simulation is
   * watched from the moment it is dispatched. Checkpoints rather than a
   * spinner, and *not* the pending-receipt panel: `reading` and `evaluating`
   * are the analysis doing its work, which is a different thing from a
   * prescription that never arrived.
   */
  if (analysing && steps.length === 0) {
    return (
      <div className="flex flex-col items-start gap-4">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={() => onExit(false)}
        >
          Voltar para a fila
        </Button>
        <h1 className="text-tH4 text-text-strong font-medium">
          {titleCaseName(quotation?.patientName ?? prescription?.patientName) || 'Conferindo…'}
        </h1>
        <span className="text-tP text-text-weak">
          Orçamento {detail.verification.quotation_number} · Filial{' '}
          {detail.verification.branch_code}
        </span>
        <div className="rounded-base border-stroke-weak bg-background-raised w-full border p-5">
          <Progress step={step} timeline={detail.verification.timeline} />
        </div>
        {isSuperAdmin === true && (
          /*
           * The way out of a run that is not going to finish.
           *
           * A workflow whose history the deployed code cannot replay is wedged,
           * and from here it looks exactly like one that is merely slow --
           * indefinitely. "Atualizar" cannot help: a reverify is a signal, and a
           * wedged workflow processes none.
           *
           * Operators only, and deliberately plain: nothing about a
           * pharmacist's day is improved by a button that stops the analysis
           * they are waiting for.
           */
          <div className="flex flex-col items-start gap-1">
            <Button
              variant="tertiary"
              tone="neutral"
              size="small"
              disabled={busy}
              onClick={abandon}
            >
              {busy ? 'Recolocando na fila…' : 'Parar e recolocar na fila'}
            </Button>
            <span className="text-tSmall text-text-weak">
              Para uma análise travada. A conferência volta para a fila e recomeça do zero.
            </span>
          </div>
        )}
      </div>
    )
  }

  /*
   * It stopped, and it says why.
   *
   * Checked before anything else about an empty verification, because a failed run
   * has no findings and no image either -- it used to fall through to
   * "aguardando a receita" and tell somebody to be patient about an verification that
   * had already given up. A failure rendered as patience is the worst of the
   * failure modes here.
   */
  if (detail.verification.status === 'failed' || detail.verification.status === 'cancelled') {
    return (
      <div className="flex flex-col items-start gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={() => onExit(false)}
        >
          Voltar para a fila
        </Button>
        <h1 className="text-tH4 text-text-strong">
          {detail.verification.status === 'cancelled'
            ? 'Conferência cancelada'
            : 'A conferência falhou'}
        </h1>
        <p className="text-tP text-text-weak max-w-[70ch]">
          {detail.verification.failure_reason ??
            'A análise parou antes de terminar e não registrou o motivo.'}
        </p>
        <Button variant="primary" tone="brand" size="small" disabled={busy} onClick={reverify}>
          {busy ? 'Recomeçando…' : 'Tentar de novo'}
        </Button>
      </div>
    )
  }

  /**
   * The prescription never arrived.
   *
   * Distinct from "nothing to review" and from "still working": this one is
   * waiting on the pharmacy's system and will resolve itself. Telling a
   * pharmacist to move on when the image is simply late would send them away
   * from an verification that is about to need them.
   */
  if (steps.length === 0 && pageUrls.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={() => onExit(false)}
        >
          Voltar para a fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Aguardando a receita</h1>
        <p className="text-tP text-text-weak">
          A imagem da receita ainda não chegou do ERP. Assim que chegar, esta conferência entra na
          fila normalmente — não é preciso fazer nada.
        </p>
      </div>
    )
  }

  if (current === undefined) {
    return (
      <div className="flex flex-col items-start gap-3">
        <h1 className="text-tH4 text-text-strong">Nada a conferir</h1>
        <p className="text-tP text-text-weak">
          Esta requisição não tem itens nem verificações registradas.
        </p>
        <Button variant="primary" tone="brand" size="small" onClick={() => onExit(true)}>
          Voltar para a fila
        </Button>
      </div>
    )
  }

  const isLast = index === steps.length - 1
  const heldByOther = claim.holder !== undefined && !claim.mine
  /*
   * Nothing on this quotation was a receita.
   *
   * Drawn as every other conferência is -- the header, the steps, the boards,
   * Atualizar -- with the orçamento on the boards and the reason where the
   * receita would be. It used to be a screen of its own with none of those,
   * on the reasoning that a reverify reaches the same answer. It does not
   * when the pharmacy attaches the receita afterwards, and that is exactly
   * the case somebody opens one of these for (#300).
   */
  const noReceita = detail.verification.status === 'discarded'

  const answeredKeys = new Set(decisions.keys())
  const unresolved = unresolvedStepIndexes(steps, answeredKeys)
  // What is left to answer, counted the way the server counts it when it
  // decides whether this may be concluded.
  const pending = unresolvedFindingCount(steps, answeredKeys)
  /** The divergence the step is about, on the divergence-only pass. */
  const divergence = walkingDivergences ? current.divergence : undefined
  // Held on the prescription: everything the step opens on is pinned.
  const divergencePins = divergence === undefined ? [] : openingPins(current)
  const divergencePinned =
    divergencePins.length > 0 && divergencePins.every((one) => pinnedKeys.has(one.key))
  // The preparation's own context, or every preparation's on a step about none.
  const contextPreparations: ContextPreparation[] = (
    current.item !== undefined ? [current] : allSteps.filter((one) => one.kind === 'item')
  ).flatMap((entry) => {
    const preparation = quotation?.preparations.find((one) => one.id === entry.item?.preparationId)
    return preparation === undefined
      ? []
      : [{ preparation, title: entry.title, labelLines: labelLinesOf(preparation) }]
  })

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/*
        How this reading compared to its answer key.
        
        Here as well as on the full view, because this is the screen
        `/verifications/:id` actually opens -- the other is `/verifications/:id/full`, which
        is not where anybody lands from a simulation.
      */}
      {showingHistory && (
        <ConferenceHistory verificationId={id} onClose={() => setShowingHistory(false)} />
      )}

      {choosingGoldenSet && (
        <AddToGoldenSet
          verificationId={id}
          onClose={() => setChoosingGoldenSet(false)}
          onOpenCase={onOpenCase}
          onOpenResolutionCase={onOpenResolutionCase}
        />
      )}

      {showingReviewResult && (
        <AdversarialReviewModal
          run={review.run}
          starting={review.starting}
          error={review.error}
          onRun={analysing ? undefined : runReview}
          onWalk={walk.disputed.length === 0 ? undefined : () => walkReview(0)}
          steps={allSteps}
          onAssess={(disagreement, assessment) => void review.assess(disagreement, assessment)}
          onJump={jumpToDisagreement}
          onClose={() => setShowingReviewResult(false)}
        />
      )}

      {walk.walking &&
        review.run != null &&
        // Under a modal the balloon would be pointing at a page nobody sees.
        !showingReviewResult &&
        !choosingGoldenSet && (
          <AdversarialReviewTour
            run={review.run}
            tour={tour}
            at={tourAt}
            onAssess={(disagreement, assessment) => void review.assess(disagreement, assessment)}
            onGo={goToStop}
            onLeave={leaveReview}
          />
        )}

      {showingReading && (
        <ReaderObject
          value={detail?.verification.prescription}
          label={`Orçamento ${detail?.verification.quotation_number ?? ''} · Filial ${detail?.verification.branch_code ?? ''}`}
          onClose={() => setShowingReading(false)}
        />
      )}

      {coaching && (
        <Coachmark
          // The decision on a row still to answer, not the first decision on
          // the page: an answered row keeps its cell, and pointing at that
          // one explained buttons that were no longer there.
          selector='[data-needs-decision="true"] [data-coach="decision"]'
          title="Como responder uma divergência"
          onDismiss={() => {
            setCoaching(false)
            focusFirstRow()
          }}
          onNeverAgain={() => {
            dismissCoachmarkForever(COACH_KEY)
            setCoaching(false)
            focusFirstRow()
          }}
        >
          <p>
            <strong>Corrigir no sistema</strong> — a divergência procede: o orçamento está errado e
            será corrigido no ERP. A conferência fica aguardando essa correção.
            <span className="text-tSmall text-text-weak mt-1 block">
              Teclado: <kbd className={KBD}>A</kbd> ou <kbd className={KBD}>Enter</kbd> na linha.
            </span>
          </p>
          <p>
            <strong>Não é divergência</strong> — o orçamento já está certo e o apontamento foi
            nosso. Vamos pedir uma explicação curta, para descobrirmos onde erramos.
            <span className="text-tSmall text-text-weak mt-1 block">
              Teclado: <kbd className={KBD}>X</kbd> ou <kbd className={KBD}>Backspace</kbd> na
              linha.
            </span>
          </p>
        </Coachmark>
      )}

      {detail.goldenCase !== undefined && (
        <GoldenCaseScore source={detail.goldenCase} onOpenCase={onOpenCase} />
      )}

      {detail.copiedFrom !== undefined && (
        <CopiedFromNotice
          from={detail.copiedFrom}
          originalHref={
            onOpenInAccount === undefined || detail.copiedFrom.verificationId === null
              ? undefined
              : conferenciaPath(detail.copiedFrom.verificationId)
          }
          onOpenOriginal={(event) => {
            const from = detail.copiedFrom
            if (from === undefined || from.verificationId === null) return
            onOpenInAccount?.(from.accountId, from.verificationId, event)
          }}
        />
      )}

      {/* From the status on screen, not only the server's word: the press that
          asks for the reverify changes the status before the detail is read again. */}
      {detail.outdatedEvaluation !== undefined &&
        status === 'awaiting_verifier' &&
        // Walking a review is reading how we judged, and nothing there may start a run.
        !walk.walking && (
          <OutdatedEvaluationNotice
            outdated={detail.outdatedEvaluation}
            busy={busy || analysing}
            onReverify={() => void reverify()}
          />
        )}

      {copyToSimulation.error !== undefined && (
        <p className="text-tP text-text-error">
          Não foi possível copiar para simulação: {copyToSimulation.error}
        </p>
      )}

      {/*
        Somebody else is on this one.

        Said rather than enforced: the screen stays fully usable, because
        locking a pharmacist out of a conferência they were asked to look at is
        a worse failure than two people reaching the same conclusion. What the
        claim actually prevents is the *queue* handing it to a second person as
        their next item.
      */}
      {heldByOther && (
        <div
          role="status"
          className="border-stroke-warning-weak bg-fill-warning-weak text-tP text-text-strong rounded-[8px] border px-4 py-3"
        >
          <strong>{claim.holder}</strong> está conferindo este orçamento agora. Você pode abrir e
          ver, mas evite registrar decisões em paralelo.
        </div>
      )}

      {/* The patient's name is the heading, with the numbers beneath: a
          pharmacist knows which verification they are in by whose it is. */}
      <div className="flex flex-row items-center gap-5">
        <Button
          size="icon"
          variant="secondary"
          tone="brand"
          aria-label="Voltar para a fila"
          leadingIcon={icons.back}
          onClick={() => onExit(false)}
        />
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="text-tH4 text-text-strong font-medium">
            {titleCaseName(quotation?.patientName ?? prescription?.patientName) ||
              'Paciente não identificado'}
          </h1>
          <span className="text-tP text-text-weak flex flex-wrap items-center gap-2">
            <span>
              Orçamento {detail.verification.quotation_number} · Filial{' '}
              {detail.verification.branch_code}
            </span>
            {/*
              The same two badges the queue draws, in the same order: where the
              work is, then what status it is in.

              Somebody arriving from the queue is following one thing, and the
              screen it opens should not make them work out whether it is still
              where and how they left it -- which, after a reverify or after
              somebody else concluded it, is a real question with a real wrong
              answer.
            */}
            {stagePositions !== undefined && (
              <span className="bg-fill-weak text-tSmall text-text-weak inline-flex items-center rounded-full px-2 py-[2px]">
                {STAGE_LABEL[stageOf(detail.verification.stage_pos ?? 0, stagePositions)]}
              </span>
            )}
            <StatusPopover verificationId={id} status={detail.verification.status} />
          </span>
        </div>
        <GoToRequisition inputRef={goToRef} />
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {analysing && (
        /*
         * Re-checking something already on screen.
         *
         * The findings below are last run's until this one lands, and they are
         * still worth looking at -- blanking the page to a progress strip
         * takes away work somebody was in the middle of, to report news they
         * asked for. It says what it is doing, at the top, and leaves the rest
         * alone.
         */
        <div className="rounded-base border-stroke-weak bg-background-raised border p-4">
          <Progress step={step} action="Atualizando" />
        </div>
      )}

      {/* Fills what the header leaves. Below `lg` the panels stack and this
          scrolls instead, because a pinned footer inside a stacked column
          leaves the card a few centimetres tall. */}
      <div className="flex min-h-0 flex-1 items-stretch gap-4 overflow-y-auto max-lg:flex-col lg:overflow-visible">
        {/*
          Half, and never more.

          Both panes are `flex-1`, which is an equal split only while neither
          is pushed wider by its own content. The comparison pane clips, so
          it cannot be; this one could -- a long ingredient list on the other
          side, a wide image here -- and it grew past half the row, with the
          board that is the work squeezed into what was left. `min-w-0` lets
          it shrink; `basis-0` makes the halves halves.
        */}
        <section
          // Named for the adversarial review's tour, which keeps it lit.
          data-review-receita=""
          className="rounded-base border-stroke-weak bg-background-raised shadow-raised flex min-h-0 min-w-0 flex-1 basis-0 flex-col border"
        >
          <CardHeader
            {...PANEL.prescription}
            beside={
              <ManualReceitaMark
                manual={manual}
                shownAttachmentId={shownAttachment}
                disabled={attachDisabled}
              />
            }
          >
            <AttachReceitaButton manual={manual} disabled={attachDisabled} />
          </CardHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-5">
            <ManualAttachmentError manual={manual} />
            {/* A receita just attached is shown while it is being read. */}
            {noReceita && pageUrls.length === 0 ? (
              <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-1 border p-6">
                <p className="text-tP text-text-strong">Sem receita para conferir</p>
                <p className="text-tP text-text-weak">
                  {detail.verification.failure_reason ??
                    'Nenhum dos anexos deste orçamento é uma receita, então não há o que conferir.'}
                </p>
                <p className="text-tP text-text-weak">
                  Se a receita foi anexada no ERP depois, use Atualizar para procurá-la de novo. Se
                  ela não está no ERP, ou não foi reconhecida, use Anexar receita.
                </p>
              </div>
            ) : (
              <PrescriptionViewer
                fill
                onShownChange={setShownAttachment}
                bringForward={manual.latest}
                pageUrls={pageUrls}
                pagesStatus={pagesStatus}
                fileBase={prescriptionFileBase(detail.verification)}
                blocks={detail.blocks}
                // The chosen row when there is one, the whole step otherwise.
                // Not having to find the region yourself is most of what
                // "guided" means; not having to find it among four overlapping
                // rectangles is the rest.
                // Hover wins over the pin, which wins over the step: pointing at
                // a row is the most recent thing you asked for.
                // What is chosen frames the view; what is pointed at is drawn
                // on top of it. Pointing at a row while four are pinned must
                // neither hide the other three nor move the page.
                // Drawn: everything pinned, plus whatever is being pointed at.
                // Drawn: what is pinned, and what is being pointed at. Never the
                // whole step's regions as a fallback -- that put rectangles on the
                // page with no row marked beside them, and the two panes disagreed.
                highlighted={[...(pinnedRegions ?? []), ...(preview?.regions ?? [])]}
                emphasised={preview?.regions}
                // Each block wears its own row's decision. One tone for the whole
                // selection meant a clean row and a flagged one, pinned
                // together, were drawn the same on the page while the
                // comparison side told them apart.
                errorRefs={errorRegions}
                // Framed: what you are pointing at, or what you chose. Hovering
                // zooms to the row, which is the whole use of hovering -- a
                // rectangle off the edge of the viewport is not an answer.
                // A row hover zooms; pointing at the page does not. Moving the
                // prescription under the pointer that is reading it is the one
                // thing this must never do.
                // Nothing chosen, nothing pointed at: the page stays at rest.
                // Framing the whole step's regions zoomed in on blocks nobody had
                // asked for -- and on a step whose only finding has no block, on
                // whatever the previous step had left.
                frameOn={
                  (preview?.from === 'row' ? preview.regions : undefined) ?? pinnedRegions ?? []
                }
                /* The reverse of the row hover, and only while nothing is
                 pinned: once something is chosen the page is answering a
                 question already asked. */
                // Only blocks this step has something to say about. A page is
                // mostly letterhead and addresses; offering those as targets
                // promises an answer that does not exist.
                hoverable={[...rowsByBlock.keys()]}
                // Only this ingredient knows whether anything is pinned; the
                // viewer cannot tell, because a hover fills the same set.
                hoverActive={pinned.size === 0}
                /* The page is a second pin column: clicking a block pins its
                 row, clicking it again unpins. Same toggle, same holder. */
                onClickBlock={(ref) => {
                  const row = rowsByBlock.get(ref)
                  if (row === undefined) return
                  setTouched(true)
                  setPreview(undefined)
                  setPinned((currentPins) => {
                    const next = new Map(currentPins)
                    /*
                     * Whatever is pinned *here* comes off first.
                     *
                     * The index names one row per block, and more than one row
                     * can cite it. Toggling only that one meant clicking a
                     * rectangle somebody else had pinned added a second pin on
                     * top rather than clearing what was there -- so the visible
                     * rectangle was never the one the click could remove.
                     */
                    const alreadyHere = [...next.entries()].filter(([, pin]) =>
                      pin.regions.includes(ref),
                    )
                    if (alreadyHere.length > 0) {
                      for (const [key] of alreadyHere) next.delete(key)
                      return next
                    }
                    next.set(row.key, { regions: row.regions, tone: row.tone })
                    return next
                  })
                }}
                onHoverBlock={(ref) => {
                  const row = ref === undefined ? undefined : rowsByBlock.get(ref)
                  setPreview(
                    row === undefined
                      ? undefined
                      : { key: row.key, regions: row.regions, tone: row.tone, from: 'block' },
                  )
                }}
                // The rectangle wears the chosen row's colour, not the step's:
                // a clean ingredient inside a flagged preparation is not itself
                // an accusation.
                tone={
                  preview?.tone ?? pinnedTone ?? (current.status === 'error' ? 'error' : 'brand')
                }
              />
            )}
          </div>
        </section>

        <section
          ref={panelRef}
          onKeyDown={onPanelKeyDown}
          className="rounded-base border-stroke-weak bg-background-raised shadow-raised flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-hidden border"
        >
          <CardHeader
            {...PANEL.comparison}
            title={feedbackReview ? 'Comparação atual' : PANEL.comparison.title}
            // Beside the title, because it changes what the whole panel shows.
            beside={
              divergenceOffered ? (
                <DivergenceOnlyToggle on={divergencePreference} onChange={setDivergenceOnly} />
              ) : undefined
            }
          >
            {/*
              Both actions on the panel they act on, as icons.

              The histórico used to sit up beside the patient's name, which put
              a control about this comparison a long way from it. Two icon
              buttons cost the header a fraction of the room two labelled ones
              did, and the words live in the tooltip -- which is better than a
              label anyway, because it can be a sentence.
            */}
            {/* Available in every state, and provisional while the conferência
                is open: what is waiting here is carried into the histórico by
                Concluir, exactly as the decisions are. On one already concluded
                there is nothing left to carry, so it commits on the spot. */}
            {isSuperAdmin === true && (
              /*
               * Keeping this conferência as a golden-set case.
               *
               * Operators only -- the API refuses anybody else. It asks which
               * set first: there are two, they measure different things, and
               * the button used to decide for people and then show only one of
               * the two cases it had made.
               */
              <Tooltip text="Adicionar ao golden set — guarda esta conferência como rascunho no caso de leitura, no de comparação, ou nos dois.">
                <Button
                  variant="secondary"
                  tone="neutral"
                  size="icon"
                  leadingIcon={icons.goldenSet}
                  aria-label="Adicionar ao golden set"
                  onClick={() => setChoosingGoldenSet(true)}
                />
              </Tooltip>
            )}
            {isSuperAdmin === true && (
              /*
               * How this receita was read, for whoever is debugging the reader.
               *
               * Beside the golden-set button because they are the same kind of
               * act -- looking at the machine rather than at the pharmacy --
               * and both are hidden from a pharmacist for the same reason.
               */
              <Tooltip text="Objeto do leitor — o que a leitura produziu para esta conferência, em JSON.">
                <Button
                  variant="secondary"
                  tone="neutral"
                  size="icon"
                  leadingIcon={icons.readerObject}
                  aria-label="Ver o objeto do leitor"
                  onClick={() => setShowingReading(true)}
                />
              </Tooltip>
            )}
            {isSuperAdmin === true && detail.verification.simulation !== true && (
              /*
               * Running this conferência again, as a simulation (#586).
               *
               * With the others that look at the machine. Not on a simulation:
               * that one can simply be analysed again.
               */
              <CopyToSimulationControl
                copying={copyToSimulation.copying}
                made={copyToSimulation.made}
                madeHref={
                  copyToSimulation.made === undefined
                    ? undefined
                    : conferenciaPath(copyToSimulation.made.verificationId)
                }
                onCopy={() => void copyToSimulation.copy()}
                onOpenCopy={(event) => {
                  const made = copyToSimulation.made
                  if (made !== undefined) {
                    onOpenInAccount?.(made.accountId, made.verificationId, event)
                  }
                }}
              />
            )}
            {isSuperAdmin === true && (
              // Beside the other two that look at the machine rather than at
              // the pharmacy, and hidden from a pharmacist for the same reason.
              <AdversarialReviewControl
                run={review.run}
                starting={review.starting}
                analysing={analysing}
                onRun={runReview}
                onOpen={() => setShowingReviewResult(true)}
              />
            )}
            <FeedbackButton
              verificationId={id}
              comments={detail?.comments ?? []}
              settled={ENDED.has(detail?.verification.status ?? '')}
              onChanged={() => void load()}
            />
            <Tooltip text="Histórico da conferência — o que foi decidido antes, por quem e quando.">
              <Button
                variant="secondary"
                tone="neutral"
                size="icon"
                leadingIcon={icons.comparison}
                aria-label="Histórico da conferência"
                onClick={() => setShowingHistory(true)}
              />
            </Tooltip>
            <Tooltip
              text={
                analysing
                  ? 'A conferência já está sendo processada. Aguarde o resultado.'
                  : noReceita
                    ? 'Atualizar — procura de novo a receita nos anexos do orçamento no ERP.'
                    : 'Atualizar comparação — lê o orçamento de novo no ERP e reavalia.'
              }
            >
              <Button
                variant="secondary"
                tone="brand"
                size="icon"
                // Not from inside the walk: a refresh rewrites the findings the
                // review is pointing at.
                disabled={busy || analysing || walk.walking}
                leadingIcon={busy || analysing ? icons.busy : icons.refresh}
                aria-label={busy || analysing ? 'Atualizando a comparação' : 'Atualizar comparação'}
                onClick={reverify}
              />
            </Tooltip>
          </CardHeader>

          {offersCorrectionView && !walk.walking && (
            <div
              role="tablist"
              aria-label="Visão"
              className="border-stroke-weak flex gap-1 border-b px-5"
            >
              {[
                { value: ReviewView.Correction, text: `Correção (${toCorrect.length})` },
                { value: ReviewView.Comparison, text: 'Conferência' },
              ].map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={view === tab.value}
                  className={`text-tPSemi -mb-px border-b-2 px-3 py-2 ${FOCUS_RING} ${
                    view === tab.value
                      ? 'border-stroke-brand-strong text-text-strong'
                      : 'text-text-weak hover:text-text-strong border-transparent'
                  }`}
                  onClick={() => switchView(tab.value)}
                >
                  {tab.text}
                </button>
              ))}
            </div>
          )}

          {divergenceOnly && (
            <DivergenceOnlyNotice
              hidden={entriesWithoutDivergence(allSteps)}
              divergences={divergenceSteps.length}
              onShowAll={() => setDivergenceOnly(false)}
            />
          )}

          <ProgressTrack
            steps={steps.map((step) => ({
              id: step.id,
              title: step.badge === undefined ? step.title : `${step.badge} · ${step.title}`,
              tone: walkingDivergences ? divergenceRailTone(step, answeredKeys) : railTone(step),
            }))}
            index={index}
            visited={visited}
            onJump={goTo}
            counterText={
              walkingDivergences
                ? divergenceCounterText(steps, index, answeredKeys)
                : railCounterText(railCounts(steps, visited, index))
            }
            ariaLabel={walkingDivergences ? 'Divergências da conferência' : 'Passos da conferência'}
          />

          {/* The only part that scrolls: the rail above and the decision
              below stay where they are. */}
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {noReceita ? (
              <div className="flex flex-col gap-3">
                <QuotationStep step={current} quotation={quotation} />
                {current?.item !== undefined && (
                  <LabelPanel lines={labelLinesOf(currentPreparation)} />
                )}
                {current?.item !== undefined && (
                  <ProductionSheet preparation={currentPreparation} sheet={productionSheet} />
                )}
                {current?.item !== undefined && (
                  <RuleOutcomes
                    outcomes={detail?.ruleOutcomes}
                    preparationId={currentPreparation?.id}
                  />
                )}
                {quotation === undefined && (
                  // Fetched before the receita is looked for, so absence means
                  // the ERP read failed rather than that the orçamento is empty.
                  <p className="text-tSmall text-text-weak">
                    Os dados do orçamento não foram lidos do ERP para esta conferência.
                  </p>
                )}
              </div>
            ) : (
              // Keyed by step on the divergence-only pass, so a context
              // section opened on one step is closed again on the next.
              <div key={divergence?.fingerprint} className="contents">
                {/* Named so the tour has somewhere to point when a dispute is
                    about a row that was never drawn. */}
                <div data-review-card="">
                  {divergence !== undefined ? (
                    /* The divergence-only pass draws its one divergence on its
                       own card, not the comparison table with the rest hidden. */
                    <DivergenceCard
                      finding={divergence}
                      item={current.item}
                      where={current.title}
                      recorded={decisions.get(divergence.fingerprint)}
                      busy={busy || analysing}
                      readOnly={locked || walk.walking}
                      onDecision={record}
                      onClearDecision={clearDecision}
                      keyedCodes={detail?.keyedCodes ?? {}}
                      onMarkExcipient={(ingredientId, productName, fingerprints) =>
                        void markExcipient(ingredientId, productName, fingerprints)
                      }
                      pinned={divergencePinned}
                      onTogglePin={() => {
                        setTouched(true)
                        setPinned(divergencePinned ? new Map() : openingPinMap(current))
                      }}
                    />
                  ) : (
                    <ComparisonCard
                      title={current.title}
                      badge={current.badge}
                      findings={current.findings}
                      // Correção shows what was marked and nothing to read past.
                      focus={showingCorrections ? marked : undefined}
                      item={current.item}
                      decisions={decisions}
                      // Walking a review is reading how we judged, never judging.
                      readOnly={locked || walk.walking}
                      /*
                Locked while the analysis runs.
                
                A decision given now is a decision about findings that are being
                rewritten underneath it: the reverify re-scores everything, and
                an answer keyed to a fingerprint that is about to change is
                discarded the moment it lands. Letting somebody work through
                eight differences and then quietly dropping the lot is worse
                than making them wait a few seconds.
              */
                      busy={busy || analysing}
                      onDecision={record}
                      onClearDecision={clearDecision}
                      located={pinnedKeys}
                      knownProducts={knownProducts}
                      keyedCodes={detail?.keyedCodes ?? {}}
                      onMarkExcipient={(ingredientId, productName, fingerprints) =>
                        void markExcipient(ingredientId, productName, fingerprints)
                      }
                      onLocate={(key, regions, tone) => {
                        setTouched(true)
                        if (key === undefined) return setPinned(new Map())
                        setPinned((current) => {
                          const next = new Map(current)
                          // Toggling: a second click on a pinned row unpins it, which
                          // is the only way back to fewer than you have.
                          if (next.has(key)) next.delete(key)
                          else next.set(key, { regions, tone })
                          return next
                        })
                      }}
                      onPreview={(key, regions, tone) =>
                        setPreview(
                          regions === undefined ? undefined : { key, regions, tone, from: 'row' },
                        )
                      }
                      previewKey={preview?.key}
                    />
                  )}
                </div>

                {/* The printed rótulo, after the card's volume and before the
                ficha: shown for the conferente to check by eye, never
                compared (#438). */}
                {current.item !== undefined && !showingCorrections && divergence === undefined && (
                  <LabelPanel lines={labelLinesOf(currentPreparation)} />
                )}

                {/* Every line the ERP keyed for this item, typed, with what is
                weighed and the product's observação.
                Collapsed: reference for the bench, not a comparison. */}
                {current.item !== undefined && !showingCorrections && divergence === undefined && (
                  <ProductionSheet preparation={currentPreparation} sheet={productionSheet} />
                )}
                {current.item !== undefined && !showingCorrections && divergence === undefined && (
                  <RuleOutcomes
                    outcomes={detail?.ruleOutcomes}
                    preparationId={currentPreparation?.id}
                  />
                )}
                {divergence !== undefined && (
                  <div className="mt-4">
                    <DivergenceContext
                      preparations={contextPreparations}
                      sheet={productionSheet}
                      outcomes={detail?.ruleOutcomes}
                      labelsOnly={current.item === undefined}
                    />
                  </div>
                )}
                {current.findings.length === 0 && (
                  <p className="text-tP text-text-weak">
                    Nada a comparar aqui. Confira na receita e siga.
                  </p>
                )}
              </div>
            )}
          </div>

          {confirmingExit && (
            <div
              role="alert"
              className="border-stroke-warning-weak bg-fill-warning-weak flex shrink-0 flex-col gap-3 border-t p-5"
            >
              <div className="flex flex-col gap-1">
                <span className="text-tH6 text-text-strong">
                  {pending === 1
                    ? '1 apontamento ainda sem resposta'
                    : `${pending} apontamentos ainda sem resposta`}
                </span>
                {/*
                  No third way out.

                  "Concluir mesmo assim" let somebody sign off on differences
                  nobody had looked at -- and a conferência marked conferida is
                  a claim the queue repeats and the requisição carries onward.
                  Leaving it open is fine and expected; saying it was checked
                  when it was not is the one thing that must not be possible.
                */}
                <span className="text-tP text-text-weak">
                  Uma conferência só pode ser concluída depois que todos os apontamentos tiverem
                  resposta. Você pode deixá-la como está e voltar depois.
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  ref={reviewPendingRef}
                  variant="primary"
                  tone="brand"
                  size="small"
                  // Tab moves between the two answers; only Enter gives one.
                  onKeyDown={(event) => {
                    if (event.key === 'Tab' && !event.shiftKey) {
                      event.preventDefault()
                      leaveAsIsRef.current?.focus()
                    }
                  }}
                  onClick={() => {
                    setConfirmingExit(false)
                    // The first one they have not answered, not the first one
                    // flagged: somewhere to resume, not a list to re-read.
                    // The hint, if it is still shown, hands focus on when it
                    // closes; otherwise the step's first row takes it now.
                    goTo(unresolved[0]!)
                    // Directly, not through the step effect: the pendência
                    // may be on the step already open, and then no step
                    // changes and that effect never runs.
                    if (coachmarkDismissed(COACH_KEY)) focusFirstRow()
                    else setCoaching(true)
                  }}
                >
                  Revisar pendências
                </Button>
                <Button
                  ref={leaveAsIsRef}
                  variant="secondary"
                  tone="neutral"
                  size="small"
                  onKeyDown={(event) => {
                    if (event.key === 'Tab') {
                      event.preventDefault()
                      reviewPendingRef.current?.focus()
                    }
                  }}
                  onClick={() => {
                    setConfirmingExit(false)
                    // The conferência stays as it is: no sign-off, nothing
                    // recorded, everything still to answer. And on to the
                    // next one, the same hand-off concluding makes.
                    void moveOn()
                  }}
                >
                  Deixar como está
                </Button>
              </div>
            </div>
          )}

          <div className="border-stroke-weak bg-background-sunken flex shrink-0 items-center justify-between gap-2 rounded-b-[8px] border-t p-5">
            <Button
              variant="secondary"
              tone="neutral"
              size="small"
              disabled={index === 0}
              onClick={() => goTo(index - 1)}
            >
              Anterior
            </Button>
            {/* The keys, where the hands are: a word in the footer, the list
                on hover. The whole legend did not fit between two buttons. */}
            <Tooltip
              text={
                walkingDivergences
                  ? [
                      '← → ↑ ↓  ou Tab  divergência anterior e próxima',
                      'Enter ou A  corrigir no sistema e ir para a próxima',
                      'Delete, Backspace ou X  não é divergência',
                      'Z  desfazer a resposta',
                      'Espaço  fixar na receita',
                      'Esc  limpar o que está fixado',
                      `${GO_TO_REQUISITION_KEY}  ir para outra conferência (número ou código de barras)`,
                    ]
                  : [
                      '← →  passos',
                      '↑ ↓  ou Tab  próximo e anterior apontamento, respondido ou não',
                      'Enter ou A  corrigir',
                      'Delete, Backspace ou X  não é divergência',
                      'Z  desfazer a resposta',
                      'Espaço  fixar na receita',
                      'Esc  limpar o que está fixado',
                      `${GO_TO_REQUISITION_KEY}  ir para outra conferência (número ou código de barras)`,
                    ]
              }
              placement="top"
            >
              <span
                className="text-tSmall text-text-weak inline-flex cursor-default items-center gap-1"
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={icons.info} className="text-icon-neutral text-xs" />
                Atalhos de teclado
              </span>
            </Tooltip>
            <Button
              ref={concludeRef}
              variant="primary"
              tone="brand"
              size="small"
              /*
               * The end of the step, walked like a row: ↑ and Shift+Tab, ↓
               * and Tab. On the last step forward stays here: there is
               * nowhere further to go, and only Enter concludes.
               */
              onKeyDown={(event) => {
                const intent = rowIntent(
                  event.key,
                  event.shiftKey,
                  event.ctrlKey || event.metaKey || event.altKey,
                )
                if (intent === 'previous-row') {
                  // Back into the step: its last question, answered or not,
                  // or the previous step's if it has none.
                  event.preventDefault()
                  const all = stops()
                  const last = all[all.length - 1]
                  if (last !== undefined) last.focus()
                  else if (index > 0) {
                    pendingFocus.current = 'last'
                    goTo(index - 1)
                  }
                  return
                }
                if (intent !== 'next-row') return
                event.preventDefault()
                /*
                 * Into this step while it still has a question open: Enter
                 * here moves to the next step and leaves the focus on this
                 * button, so going forward from it skipped that step's rows
                 * altogether. Otherwise on to the next step -- or, on the
                 * last, nowhere.
                 */
                const open = openStops()[0]
                if (open !== undefined) {
                  open.focus()
                  return
                }
                if (!isLast) {
                  pendingFocus.current = 'land'
                  goTo(index + 1)
                }
              }}
              /*
                Only on the last step, where this concludes.
                
                On every other step it is navigation, and reading ahead while
                the analysis runs costs nothing. Blocking that too would trap
                somebody in the middle of a conferência for the seconds a
                reverify takes.
              */
              disabled={isLast && (busy || analysing)}
              onClick={() => {
                if (!isLast) {
                  goTo(index + 1)
                  return
                }
                // The walk ends where it began: back on the conferência.
                if (walk.walking) {
                  leaveReview()
                  return
                }
                // Locked: the last step ends in the one thing still to do.
                if (awaitingCorrection) {
                  void reverify()
                  return
                }
                if (locked) {
                  onExit(false)
                  return
                }
                if (unresolved.length > 0) {
                  setConfirmingExit(true)
                  return
                }
                void finish()
              }}
            >
              {walk.walking
                ? isLast
                  ? 'Sair da revisão'
                  : 'Próximo item da revisão'
                : !isLast
                  ? // A step of this pass is one divergence, not an entry
                    // being signed off as reviewed.
                    walkingDivergences
                    ? 'Próxima divergência'
                    : locked
                      ? 'Próximo item'
                      : 'Revisado - próximo item'
                  : awaitingCorrection
                    ? // Whoever sees one waiting works corrections (#439): this is
                      // them saying the ERP was changed.
                      'Corrigido - enviar para reconferência'
                    : locked
                      ? 'Voltar para a fila'
                      : 'Revisado - concluir'}
            </Button>
          </div>
        </section>
      </div>
    </div>
  )
}

/**
 * The rail: a counter and a row of dots joined by a line.
 *
 * Reviewed and clean is filled with a tick, flagged is red with an
 * exclamation, not evaluated is amber with a question mark, anything not yet
 * visited is hollow. A step nobody has been on is **not** clean even when it
 * holds no findings -- otherwise the rail records what the system concluded
 * rather than what a person checked.
 */

/**
 * What a step opens with pinned.
 *
 * The divergent row, when there is exactly one -- landing on a step with its
 * own disagreement already on the page is most of what "guided" means. A map
 * because pins are a set now; a step still opens with at most one, and
 * everything after that is the reader's choice.
 */
/**
 * Where the keys put the focus once a step has rendered.
 *
 * `land`: where the work is, as ← → and arrival want it. `first` and `last`:
 * the step's first or last question, answered or not, for ↑ ↓ walking across
 * steps. `conclude`: the footer's button.
 */
type StepFocus = 'land' | 'first' | 'last' | 'conclude'

function openingPinMap(
  step: GuidedStep,
): ReadonlyMap<string, { regions: readonly string[]; tone: 'error' | 'brand' }> {
  return pinMap(openingPins(step))
}

function pinMap(
  rows: readonly OpenRow[],
): ReadonlyMap<string, { regions: readonly string[]; tone: 'error' | 'brand' }> {
  return new Map(rows.map((row) => [row.key, { regions: row.regions, tone: row.tone }]))
}

function rememberedStep(verificationId: string): number {
  try {
    const stored = window.sessionStorage.getItem(`review.step.${verificationId}`)
    const parsed = stored === null ? 0 : Number(stored)
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0
  } catch {
    return 0
  }
}

function rememberStep(verificationId: string, step: number): void {
  try {
    window.sessionStorage.setItem(`review.step.${verificationId}`, String(step))
  } catch {
    // A private window that refuses storage starts every visit at the first
    // step, which is the honest degradation.
  }
}

function forgetStep(verificationId: string): void {
  try {
    window.sessionStorage.removeItem(`review.step.${verificationId}`)
  } catch {
    // Nothing remembered, nothing to forget.
  }
}

/** Whether a role sees a status; undefined -- not yet known -- counts as yes. */
function sees(visible: readonly string[] | undefined, status: string): boolean {
  return visible === undefined || visible.includes(status)
}

/** A conferência's address by id: the form a simulation is always opened by. */
function conferenciaPath(verificationId: string): string {
  return toPath({ name: 'verification', at: { kind: 'identified', id: verificationId } })
}
